// ============================================================
//  src/pages/bots/tabs/ScheduledTab.js
//  — Week-view calendar + list, full replacement
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listScheduled, createScheduled,
  cancelScheduled, deleteScheduled,
  getBillingOverview,
} from '../../../api/client';
import { listBotUsers } from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';
import './ScheduledTab.css';

// ── Helpers ───────────────────────────────────────────────────
function parseDate(dt) {
  if (!dt) return null;
  return new Date(dt.replace(' ', 'T'));
}
function fmt(dt) {
  const d = parseDate(dt);
  if (!d) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
function fmtTime(dt) {
  const d = parseDate(dt);
  if (!d) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
function fmtDay(date) {
  return date.toLocaleDateString(undefined, { weekday: 'short' });
}
function fmtDayNum(date) {
  return date.getDate();
}
function fmtMonth(date) {
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}
function isToday(date) { return sameDay(date, new Date()); }

const STATUS_STYLE = {
  pending:   { bg: '#eff6ff', color: '#2563eb', label: 'Pending'   },
  sent:      { bg: '#f0fdf4', color: '#16a34a', label: 'Sent'      },
  failed:    { bg: '#fef2f2', color: '#dc2626', label: 'Failed'    },
  cancelled: { bg: '#f9fafb', color: '#6b7280', label: 'Cancelled' },
};
const STATUS_DOT = {
  pending:   '#2563eb',
  sent:      '#16a34a',
  failed:    '#dc2626',
  cancelled: '#9ca3af',
};

// ── Week-view calendar ────────────────────────────────────────
function WeekCalendar({ messages, onDayClick, selectedDay }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    // go back to Monday
    const day = d.getDay(); // 0=Sun
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return d;
  });

  // Build 7 days from weekStart
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  // Index messages by day
  const byDay = {};
  messages.forEach(m => {
    const d = parseDate(m.smScheduledAt);
    if (!d) return;
    const key = startOfDay(d).toDateString();
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(m);
  });

  function prevWeek() {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  }
  function nextWeek() {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  }
  function goToday() {
    const d = new Date();
    d.setHours(0,0,0,0);
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    setWeekStart(d);
  }

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return (
    <div className="sc-calendar">
      {/* Calendar nav */}
      <div className="sc-cal-nav">
        <div className="sc-cal-title">{fmtMonth(weekStart)}</div>
        <div className="sc-cal-nav-btns">
          <button className="sc-nav-btn" onClick={prevWeek} title="Previous week">‹</button>
          <button className="sc-nav-btn sc-nav-today" onClick={goToday}>Today</button>
          <button className="sc-nav-btn" onClick={nextWeek} title="Next week">›</button>
        </div>
      </div>

      {/* Day columns */}
      <div className="sc-week-grid">
        {days.map((day, i) => {
          const key      = startOfDay(day).toDateString();
          const dayMsgs  = byDay[key] ?? [];
          const today    = isToday(day);
          const selected = selectedDay && sameDay(day, selectedDay);
          const hasPending = dayMsgs.some(m => m.smStatus === 'pending');

          return (
            <button
              key={i}
              className={`sc-day-col${today ? ' sc-day-today' : ''}${selected ? ' sc-day-selected' : ''}${dayMsgs.length ? ' sc-day-has-items' : ''}`}
              onClick={() => onDayClick(dayMsgs.length ? day : null)}
            >
              <div className="sc-day-label">{fmtDay(day)}</div>
              <div className={`sc-day-num${today ? ' sc-day-num-today' : ''}`}>{fmtDayNum(day)}</div>

              {/* Event dots / pills */}
              <div className="sc-day-events">
                {dayMsgs.length === 0 && (
                  <div className="sc-day-empty-line" />
                )}
                {dayMsgs.slice(0, 3).map((m, j) => (
                  <div
                    key={j}
                    className="sc-event-pill"
                    style={{ background: STATUS_DOT[m.smStatus] + '22', borderLeft: `3px solid ${STATUS_DOT[m.smStatus]}` }}
                    title={`${fmtTime(m.smScheduledAt)} — ${m.smMessage?.slice(0, 60)}`}
                  >
                    <span className="sc-event-time">{fmtTime(m.smScheduledAt)}</span>
                    <span className="sc-event-text">{m.smMessage?.slice(0, 18) || '—'}</span>
                  </div>
                ))}
                {dayMsgs.length > 3 && (
                  <div className="sc-event-more">+{dayMsgs.length - 3} more</div>
                )}
              </div>

              {hasPending && <div className="sc-day-badge" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Button Builder ────────────────────────────────────────────
function ButtonBuilder({ buttons, onChange }) {
  const add = () => {
    if (buttons.length >= 3) return;
    onChange([...buttons, { type: 'reply', title: '', value: '' }]);
  };
  const remove = (i) => onChange(buttons.filter((_, idx) => idx !== i));
  const update = (i, fields) =>
    onChange(buttons.map((b, idx) => idx === i ? { ...b, ...fields } : b));

  const hasUrl    = buttons.some(b => b.type === 'url');
  const hasNonUrl = buttons.some(b => b.type !== 'url');

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Buttons (optional, max 3)
        </span>
        {buttons.length < 3 && (
          <button type="button" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={add}>
            + Add Button
          </button>
        )}
      </div>
      {buttons.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>No buttons — plain text message.</div>
      )}
      {buttons.map((btn, i) => (
        <div key={i} style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 11, fontWeight: 800, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {i + 1}
            </div>
            <input className="form-input" placeholder="Button label (max 20 chars)" maxLength={20} value={btn.title}
              onChange={e => update(i, { title: e.target.value })} style={{ flex: 1 }} />
            <button type="button" className="icon-btn danger" onClick={() => remove(i)}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: btn.type !== 'reply' ? 10 : 0 }}>
            {[
              { key: 'reply', label: '💬 Reply',   disabled: false },
              { key: 'url',   label: '🔗 URL',     disabled: hasNonUrl && btn.type !== 'url' },
            ].map(opt => (
              <button key={opt.key} type="button" disabled={opt.disabled}
                onClick={() => update(i, { type: opt.key, value: '' })}
                style={{
                  flex: 1, padding: '6px 8px', fontSize: 12, fontWeight: 600,
                  borderRadius: 8, border: '1.5px solid', cursor: opt.disabled ? 'not-allowed' : 'pointer',
                  opacity: opt.disabled ? 0.4 : 1,
                  borderColor: btn.type === opt.key ? 'var(--primary)' : 'var(--border)',
                  background:  btn.type === opt.key ? 'var(--primary-light)' : 'var(--white)',
                  color:       btn.type === opt.key ? 'var(--primary)' : 'var(--text-mid)',
                }}>{opt.label}</button>
            ))}
          </div>
          {btn.type === 'url' && (
            <input className="form-input" placeholder="https://example.com" value={btn.value}
              onChange={e => update(i, { value: e.target.value })} />
          )}
        </div>
      ))}
    </div>
  );
}

// ── New Scheduled Message Modal ───────────────────────────────
function NewScheduledModal({ bId, onClose, onCreated, defaultDate }) {
  const [target,      setTarget]      = useState('all');
  const [chatId,      setChatId]      = useState('');
  const [userSearch,  setUserSearch]  = useState('');
  const [users,       setUsers]       = useState([]);
  const [showPicker,  setShowPicker]  = useState(false);
  const [message,     setMessage]     = useState('');
  const [buttons,     setButtons]     = useState([]);
  const [scheduledAt, setScheduledAt] = useState(() => {
    if (defaultDate) {
      const d = new Date(defaultDate);
      d.setHours(9, 0, 0, 0);
      return d.toISOString().slice(0, 16);
    }
    return '';
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target === 'single') {
      listBotUsers(bId, 500, 0).then(res => setUsers(res.data?.users ?? [])).catch(() => {});
    }
  }, [bId, target]);

  const minDt = new Date(Date.now() + 2 * 60000).toISOString().slice(0, 16);
  const filteredUsers = users.filter(u =>
    !userSearch ||
    (u.uName ?? '').toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.uChatId ?? '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const validateButtons = () => {
    for (const b of buttons) {
      if (!b.title.trim()) return 'All buttons must have a label';
      if (b.type === 'url') {
        if (!b.value.trim()) return 'URL buttons need a URL';
        try { new URL(b.value.trim()); } catch { return 'Invalid URL'; }
      }
    }
    if (buttons.filter(b => b.type === 'url').length > 1) return 'Only one URL button allowed';
    return null;
  };

  const handleSave = async () => {
    if (!message.trim())                     { toast.error('Message is required'); return; }
    if (!scheduledAt)                        { toast.error('Scheduled time is required'); return; }
    if (target === 'single' && !chatId)      { toast.error('Select a user'); return; }
    const btnErr = validateButtons();
    if (btnErr) { toast.error(btnErr); return; }

    const cleanButtons = buttons.map(b => ({
      type: b.type, title: b.title.trim(),
      id:   b.title.toLowerCase().replace(/\s+/g,'_'),
      ...(b.type === 'url' ? { url: b.value.trim() } : {}),
    }));

    setSaving(true);
    try {
      const res = await createScheduled({
        bId, target,
        chatId:      target === 'single' ? chatId : undefined,
        message:     message.trim(),
        scheduledAt,
        buttons:     cleanButtons.length > 0 ? cleanButtons : undefined,
      });
      if (res.data?.success) {
        toast.success('Message scheduled!');
        onCreated();
      } else {
        toast.error(res.data?.message ?? 'Failed');
      }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">🕐 Schedule Message</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Target */}
          <div className="form-group">
            <label className="form-label">Send to</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {[['all','📢 All users'],['single','👤 Single user']].map(([k,l]) => (
                <button key={k} type="button"
                  onClick={() => { setTarget(k); setChatId(''); }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: 10,
                    border: '1.5px solid', fontWeight: 600, fontSize: 13.5, cursor: 'pointer',
                    borderColor: target === k ? 'var(--primary)' : 'var(--border)',
                    background:  target === k ? 'var(--primary-light)' : 'var(--white)',
                    color:       target === k ? 'var(--primary)' : 'var(--text-mid)',
                  }}>{l}</button>
              ))}
            </div>
          </div>

          {target === 'single' && (
            <div className="form-group">
              <label className="form-label">Select user</label>
              <div style={{ position: 'relative' }}>
                <input className="form-input"
                  placeholder="Search by name or chat ID…"
                  value={chatId ? (users.find(u => u.uChatId === chatId)?.uName || chatId) : userSearch}
                  onChange={e => { setUserSearch(e.target.value); setChatId(''); setShowPicker(true); }}
                  onFocus={() => setShowPicker(true)} readOnly={!!chatId} />
                {chatId && (
                  <button onClick={() => { setChatId(''); setUserSearch(''); }}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>✕</button>
                )}
                {showPicker && !chatId && filteredUsers.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                    {filteredUsers.map(u => (
                      <div key={u.uId}
                        onMouseDown={() => { setChatId(u.uChatId); setShowPicker(false); setUserSearch(''); }}
                        style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {(u.uName?.[0] ?? u.uChatId?.[0] ?? '?').toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{u.uName || 'Anonymous'}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{u.uChatId}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea className="form-input" rows={4} placeholder="Type your message…"
              value={message} onChange={e => setMessage(e.target.value)} style={{ resize: 'vertical' }} />
          </div>

          <ButtonBuilder buttons={buttons} onChange={setButtons} />

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Send at</label>
            <input type="datetime-local" className="form-input" min={minDt}
              value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
              Times are in your server's local timezone. The cron job runs every minute.
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner"/> Scheduling…</> : '🕐 Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Scheduled Message Card ────────────────────────────────────
function ScheduledCard({ m, acting, onCancel, onDelete }) {
  const buttons = m.smButtons ? JSON.parse(m.smButtons) : [];
  const style   = STATUS_STYLE[m.smStatus] ?? STATUS_STYLE.pending;

  return (
    <div className="sc-card">
      <div className="sc-card-left">
        {/* Status dot */}
        <div className="sc-card-dot" style={{ background: STATUS_DOT[m.smStatus] }} />
        <div className="sc-card-time">
          <div className="sc-card-time-main">{fmtTime(m.smScheduledAt)}</div>
          <div className="sc-card-time-date">
            {parseDate(m.smScheduledAt)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </div>
        </div>
      </div>

      <div className="sc-card-body">
        <div className="sc-card-meta">
          <span className="sc-status-pill" style={{ background: style.bg, color: style.color }}>
            {style.label}
          </span>
          <span className="sc-card-target">
            {m.smTarget === 'all' ? '📢 All users' : `👤 ${m.smChatId}`}
          </span>
        </div>

        <div className="sc-card-message">{m.smMessage}</div>

        {buttons.length > 0 && (
          <div className="sc-card-buttons">
            {buttons.map((b, i) => (
              <span key={i} className="sc-btn-chip">
                {b.type === 'url' ? '🔗' : '💬'} {b.title}
              </span>
            ))}
          </div>
        )}

        {m.smStatus === 'failed' && m.smError && (
          <div className="sc-card-error">✕ {m.smError}</div>
        )}
        {m.smStatus === 'sent' && m.smSentAt && (
          <div className="sc-card-sent">✓ Sent {fmt(m.smSentAt)}</div>
        )}
      </div>

      <div className="sc-card-actions">
        {m.smStatus === 'pending' && (
          <button className="btn btn-outline btn-sm" disabled={!!acting[m.smId]} onClick={() => onCancel(m.smId)}>
            {acting[m.smId] ? <span className="spinner spinner-dark"/> : '✕ Cancel'}
          </button>
        )}
        {m.smStatus !== 'pending' && (
          <button className="icon-btn danger" title="Delete" disabled={!!acting[m.smId]} onClick={() => onDelete(m.smId)}>
            {acting[m.smId] ? <span className="spinner spinner-dark" style={{ width: 11, height: 11 }}/> : '🗑'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Day detail panel ──────────────────────────────────────────
function DayPanel({ day, messages, acting, onCancel, onDelete, onClose }) {
  const dayMsgs = messages.filter(m => {
    const d = parseDate(m.smScheduledAt);
    return d && sameDay(d, day);
  });

  return (
    <div className="sc-day-panel">
      <div className="sc-day-panel-hdr">
        <div className="sc-day-panel-title">
          {day.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          <span className="sc-day-panel-count">{dayMsgs.length} message{dayMsgs.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="sc-day-panel-close" onClick={onClose}>✕</button>
      </div>
      <div className="sc-day-panel-list">
        {dayMsgs.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
            No messages on this day.
          </div>
        ) : (
          dayMsgs.map(m => (
            <ScheduledCard key={m.smId} m={m} acting={acting} onCancel={onCancel} onDelete={onDelete} />
          ))
        )}
      </div>
    </div>
  );
}

// ── Main ScheduledTab ─────────────────────────────────────────
export default function ScheduledTab({ bId }) {
  const navigate = useNavigate();
  const [messages,    setMessages]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showNew,     setShowNew]     = useState(false);
  const [acting,      setActing]      = useState({});
  const [canSchedule, setCanSchedule] = useState(true);
  const [view,        setView]        = useState('calendar'); // 'calendar' | 'list'
  const [selectedDay, setSelectedDay] = useState(null);
  const [newDefaultDate, setNewDefaultDate] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listScheduled(bId), getBillingOverview()])
      .then(([schRes, billingRes]) => {
        setMessages(schRes.data?.messages ?? []);
        const plan = billingRes?.data?.current_plan;
        setCanSchedule(!!plan?.plAllowScheduled);
      })
      .catch(() => toast.error('Failed to load scheduled messages'))
      .finally(() => setLoading(false));
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (smId) => {
    setActing(a => ({ ...a, [smId]: true }));
    try {
      const res = await cancelScheduled(bId, smId);
      if (res.data?.success) {
        setMessages(prev => prev.map(m => m.smId === smId ? { ...m, smStatus: 'cancelled' } : m));
        toast.success('Message cancelled');
      } else { toast.error(res.data?.message ?? 'Failed'); }
    } catch { toast.error('Failed'); }
    setActing(a => { const n = { ...a }; delete n[smId]; return n; });
  };

  const handleDelete = async (smId) => {
    if (!window.confirm('Delete this scheduled message?')) return;
    setActing(a => ({ ...a, [smId]: true }));
    try {
      await deleteScheduled(bId, smId);
      setMessages(prev => prev.filter(m => m.smId !== smId));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
    setActing(a => { const n = { ...a }; delete n[smId]; return n; });
  };

  function handleDayClick(day) {
    if (!day) return;
    setSelectedDay(day);
  }

  function openNewOnDay(day) {
    setNewDefaultDate(day);
    setShowNew(true);
  }

  const pending = messages.filter(m => m.smStatus === 'pending');
  const history = messages.filter(m => m.smStatus !== 'pending');

  if (loading) return <div className="tab-loading"><span className="spinner spinner-dark"/> Loading…</div>;

  return (
    <div className="tab-content">
      {/* Toolbar */}
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">Scheduled Messages</div>
          <div className="tab-sub">{pending.length} pending · {history.length} in history</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          <div className="sc-view-toggle">
            <button
              className={`sc-view-btn${view === 'calendar' ? ' is-active' : ''}`}
              onClick={() => setView('calendar')}
              title="Calendar view"
            >📅 Calendar</button>
            <button
              className={`sc-view-btn${view === 'list' ? ' is-active' : ''}`}
              onClick={() => setView('list')}
              title="List view"
            >☰ List</button>
          </div>
          {canSchedule ? (
            <button className="btn btn-primary" onClick={() => { setNewDefaultDate(null); setShowNew(true); }}>
              + Schedule
            </button>
          ) : (
            <button className="btn btn-outline" onClick={() => navigate('/billing')}
              style={{ color: 'var(--text-muted)' }}>🔒 Schedule</button>
          )}
        </div>
      </div>

      {/* Upgrade banner */}
      {!canSchedule && (
        <div className="sc-upgrade-banner">
          <div style={{ fontSize: 13, color: 'var(--text-dark)' }}>
            🔒 <strong>Scheduled Messages</strong> are not included in your current plan.
          </div>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
            onClick={() => navigate('/billing')}>Upgrade</button>
        </div>
      )}

      {/* ── Calendar view ─────────────────────────────────── */}
      {view === 'calendar' && (
        <>
          <WeekCalendar
            messages={messages}
            onDayClick={handleDayClick}
            selectedDay={selectedDay}
          />

          {/* Day detail panel */}
          {selectedDay ? (
            <DayPanel
              day={selectedDay}
              messages={messages}
              acting={acting}
              onCancel={handleCancel}
              onDelete={handleDelete}
              onClose={() => setSelectedDay(null)}
            />
          ) : (
            <div className="sc-cal-hint">
              {messages.length === 0
                ? <span>No scheduled messages yet. <button className="link-btn" onClick={() => setShowNew(true)}>Schedule one →</button></span>
                : <span>Click a day with events to view details.</span>
              }
            </div>
          )}
        </>
      )}

      {/* ── List view ─────────────────────────────────────── */}
      {view === 'list' && (
        <>
          {messages.length === 0 ? (
            <div className="tab-empty">
              No scheduled messages yet.{' '}
              {canSchedule
                ? <button className="link-btn" onClick={() => setShowNew(true)}>Schedule one →</button>
                : <button className="link-btn" onClick={() => navigate('/billing')}>Upgrade to schedule →</button>
              }
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <>
                  <div className="sc-section-label">Upcoming</div>
                  <div className="sc-list">
                    {pending.map(m => (
                      <ScheduledCard key={m.smId} m={m} acting={acting} onCancel={handleCancel} onDelete={handleDelete} />
                    ))}
                  </div>
                </>
              )}
              {history.length > 0 && (
                <>
                  <div className="sc-section-label" style={{ marginTop: 24 }}>History</div>
                  <div className="sc-list">
                    {history.map(m => (
                      <ScheduledCard key={m.smId} m={m} acting={acting} onCancel={handleCancel} onDelete={handleDelete} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {showNew && canSchedule && (
        <NewScheduledModal
          bId={bId}
          defaultDate={newDefaultDate}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}